import { useState } from 'react';
import { FilterTabs, type FilterTabOption } from '../../molecules/filter-tabs/FilterTabs';
import { Pagination } from '../../molecules/pagination/Pagination';
import { type StatusFilter, type Survey } from './types';
import { SurveyRow } from './SurveyRow';

const FILTER_KEYS: StatusFilter[] = ['all', 'upcoming', 'active', 'expired'];

const FILTER_LABELS: Record<StatusFilter, string> = {
  all: 'Tous',
  upcoming: 'À venir',
  active: 'Actifs',
  expired: 'Expirés',
};
const PAGE_SIZE = 10;

type Props = {
  surveys: Survey[];
  onViewStats: (survey: Survey) => void;
  onEdit: (survey: Survey) => void;
  onDelete: (id: number) => void;
  onDuplicate: (survey: Survey) => void;
};

export function SurveyList({ surveys, onViewStats, onEdit, onDelete, onDuplicate }: Props) {
  const [filter, setFilter] = useState<StatusFilter>('all');
  const [page, setPage] = useState(1);

  const counts: Record<StatusFilter, number> = {
    all: surveys.length,
    upcoming: surveys.filter((s) => s.status === 'upcoming').length,
    active: surveys.filter((s) => s.status === 'active').length,
    expired: surveys.filter((s) => s.status === 'expired').length,
  };

  const filterOptions: FilterTabOption<StatusFilter>[] = FILTER_KEYS.map((key) => ({
    key,
    label: FILTER_LABELS[key],
    count: counts[key],
  }));

  function handleFilterChange(next: StatusFilter) {
    setFilter(next);
    setPage(1);
  }

  const filtered = filter === 'all' ? surveys : surveys.filter((s) => s.status === filter);
  const totalPages = Math.ceil(filtered.length / PAGE_SIZE);
  const visible = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  return (
    <div>
      <FilterTabs options={filterOptions} active={filter} onChange={handleFilterChange} className="mb-4" />

      {visible.length === 0 ? (
        <p className="py-12 text-center text-lg text-neutral-400">Aucun sondage trouvé.</p>
      ) : (
        <div className="border border-neutral-200 rounded-lg overflow-hidden">
          <div className="flex items-center px-4 py-2.5 bg-neutral-50 border-b border-neutral-200 text-lg font-medium text-neutral-500">
            <div className="flex-1 min-w-0 mr-4">Titre</div>
            <div className="w-28 shrink-0">Début</div>
            <div className="w-28 shrink-0">Fin</div>
            <div className="w-28 shrink-0">Questions</div>
            <div className="w-28 shrink-0">Réponses</div>
            <div className="w-28 shrink-0">Statut</div>
            <div className="w-16 shrink-0" />
          </div>
          {visible.map((survey) => (
            <SurveyRow
              key={survey.id}
              survey={survey}
              onViewStats={onViewStats}
              onEdit={onEdit}
              onDelete={onDelete}
              onDuplicate={onDuplicate}
            />
          ))}
        </div>
      )}

      <Pagination page={page} totalPages={totalPages} onChange={setPage} />
    </div>
  );
}
